import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Inbox,
  RefreshCw,
  Mail,
  Building2,
  Clock,
  Filter,
  MailCheck,
  MailX,
  Search as SearchIcon,
} from "lucide-react";

const TOPIC_COLOR = {
  general:     "text-slate-600 border-slate-200",
  feature:     "text-blue-300 border-blue-400/40",
  bug:         "text-rose-300 border-rose-400/40",
  partnership: "text-emerald-300 border-emerald-400/40",
  press:       "text-amber-300 border-amber-400/40",
  other:       "text-slate-500 border-slate-200",
};

function formatTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function AdminInboxPanel() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [topics, setTopics] = useState({});
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async (topic) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/contact-messages", {
        params: topic && topic !== "all" ? { topic } : {},
      });
      setItems(data.items || []);
      setCounts(data.counts || {});
      setTopics(data.topics || {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load inbox");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(filter); }, [load, filter]);

  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const q = query.toLowerCase();
    return items.filter(
      (m) =>
        (m.name || "").toLowerCase().includes(q) ||
        (m.email || "").toLowerCase().includes(q) ||
        (m.company || "").toLowerCase().includes(q) ||
        (m.message || "").toLowerCase().includes(q)
    );
  }, [items, query]);

  const selected = filtered.find((m) => m.id === selectedId) || filtered[0] || null;

  const topicChips = [
    { code: "all", label: "All" },
    ...Object.entries(topics).map(([code, label]) => ({ code, label })),
  ];

  return (
    <div data-testid="admin-inbox-root" className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-slate-200 pb-6">
        <div>
          <div className="overline mb-2 text-[#2563EB]">ADMIN</div>
          <h1 className="font-serif text-4xl font-semibold leading-tight text-slate-900">
            Contact Inbox
          </h1>
          <p className="mt-2 max-w-xl text-sm text-slate-500">
            Every message submitted through the public contact form. Filter by topic, search, and triage without opening the database.
          </p>
        </div>
        <button
          data-testid="admin-inbox-refresh"
          onClick={() => load(filter)}
          className="inline-flex items-center gap-2 border border-slate-200 px-3 py-2 text-xs font-medium text-slate-700 hover:border-[#2563EB] hover:text-slate-900"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* Filter chips + search */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1 text-xs text-slate-400">
          <Filter className="h-3 w-3" /> TOPIC
        </div>
        <div className="flex flex-wrap gap-2">
          {topicChips.map((t) => {
            const n = counts[t.code] ?? 0;
            const active = filter === t.code;
            return (
              <button
                key={t.code}
                data-testid={`admin-inbox-chip-${t.code}`}
                onClick={() => { setFilter(t.code); setSelectedId(null); }}
                className={`inline-flex items-center gap-2 border px-3 py-1.5 text-xs font-medium transition-all ${
                  active
                    ? "border-[#2563EB] bg-[#2563EB]/10 text-white"
                    : "border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-900"
                }`}
              >
                {t.label}
                <span className="text-slate-400">{n}</span>
              </button>
            );
          })}
        </div>
        <div className="ml-auto flex items-center gap-2 border border-slate-200 bg-white px-3 py-1.5">
          <SearchIcon className="h-3.5 w-3.5 text-slate-400" />
          <input
            data-testid="admin-inbox-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, email, message…"
            className="w-64 bg-transparent text-xs text-slate-900 placeholder:text-slate-300 focus:outline-none"
          />
        </div>
      </div>

      {/* Two-pane layout */}
      <div className="grid gap-4 lg:grid-cols-12">
        {/* List */}
        <div className="border border-slate-200 bg-white lg:col-span-5 lg:max-h-[70vh] lg:overflow-y-auto">
          {loading && items.length === 0 ? (
            <div className="p-8 text-center text-xs text-slate-400">Loading messages…</div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center gap-3 p-12 text-center text-slate-400">
              <Inbox className="h-8 w-8" strokeWidth={1.5} />
              <div className="text-sm">No messages match this filter.</div>
            </div>
          ) : (
            <ul className="divide-y divide-slate-200">
              {filtered.map((m) => {
                const active = selected?.id === m.id;
                const color = TOPIC_COLOR[m.topic] || TOPIC_COLOR.other;
                return (
                  <li key={m.id}>
                    <button
                      data-testid={`admin-inbox-item-${m.id}`}
                      onClick={() => setSelectedId(m.id)}
                      className={`block w-full px-4 py-3 text-left transition-colors ${
                        active ? "bg-[#2563EB]/10" : "hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="truncate text-sm font-medium text-slate-900">{m.name || "—"}</div>
                        <span className={`overline shrink-0 border px-1.5 py-0.5 ${color}`}>
                          {(topics[m.topic] || m.topic || "general").toUpperCase()}
                        </span>
                      </div>
                      <div className="mt-0.5 truncate text-xs text-slate-500">{m.email}</div>
                      <div className="mt-1 line-clamp-2 text-xs leading-relaxed text-slate-500">
                        {m.message}
                      </div>
                      <div className="mt-1.5 flex items-center gap-3 text-[10px] uppercase tracking-wider text-slate-300">
                        <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {formatTime(m.created_at)}</span>
                        {m.autoreply_sent ? (
                          <span className="inline-flex items-center gap-1 text-emerald-400/70"><MailCheck className="h-3 w-3" /> replied</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-slate-300"><MailX className="h-3 w-3" /> no auto-reply</span>
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Detail */}
        <div className="border border-slate-200 bg-white p-6 lg:col-span-7">
          {!selected ? (
            <div className="flex h-full items-center justify-center text-sm text-slate-400">
              Select a message to read
            </div>
          ) : (
            <div data-testid="admin-inbox-detail">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="overline text-slate-400">FROM</div>
                  <h2 className="mt-1 font-serif text-2xl font-semibold text-slate-900">{selected.name}</h2>
                  <a href={`mailto:${selected.email}`} className="mt-1 inline-flex items-center gap-1.5 text-sm text-[#93C5FD] hover:underline">
                    <Mail className="h-3.5 w-3.5" /> {selected.email}
                  </a>
                  {selected.company ? (
                    <div className="mt-1 inline-flex items-center gap-1.5 text-xs text-slate-500">
                      <Building2 className="h-3 w-3" /> {selected.company}
                    </div>
                  ) : null}
                </div>
                <div className="text-right">
                  <span className={`overline border px-2 py-1 ${TOPIC_COLOR[selected.topic] || TOPIC_COLOR.other}`}>
                    {(topics[selected.topic] || selected.topic || "general").toUpperCase()}
                  </span>
                  <div className="mt-2 text-[11px] uppercase tracking-wider text-slate-400">
                    {formatTime(selected.created_at)}
                  </div>
                </div>
              </div>

              <div className="mt-6 border-l-2 border-[#2563EB]/60 bg-[#FAFAF7] p-4">
                <div className="whitespace-pre-wrap text-sm leading-relaxed text-slate-800">
                  {selected.message}
                </div>
              </div>

              <div className="mt-6 grid gap-3 text-xs text-slate-500 sm:grid-cols-2">
                <div>
                  <div className="overline text-slate-300">AUTO-REPLY</div>
                  <div className="mt-1 text-slate-600">
                    {selected.autoreply_sent
                      ? <span className="inline-flex items-center gap-1 text-emerald-400"><MailCheck className="h-3.5 w-3.5" /> Sent ({selected.autoreply_id?.slice(0, 8)}…)</span>
                      : <span className="inline-flex items-center gap-1 text-slate-500"><MailX className="h-3.5 w-3.5" /> Not sent (Resend key missing?)</span>}
                  </div>
                </div>
                <div>
                  <div className="overline text-slate-300">IP / SIGNED-IN</div>
                  <div className="mt-1 text-slate-600">
                    {selected.ip || "—"}
                    {selected.user_email ? <span className="ml-2 text-slate-400">· {selected.user_email}</span> : null}
                  </div>
                </div>
              </div>

              <div className="mt-6 flex gap-3">
                <a
                  href={`mailto:${selected.email}?subject=Re: your message to Qualmitta`}
                  data-testid="admin-inbox-reply"
                  className="inline-flex items-center gap-2 border border-[#2563EB] bg-[#2563EB] px-4 py-2 text-sm font-semibold text-white hover:bg-[#1D4ED8]"
                >
                  <Mail className="h-4 w-4" /> Reply by email
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
